import { observer } from 'mobx-react-lite';

import { ArtifactCard, ArtifactCardSkeleton } from '@/features/artifacts';
import {
  useOpenFilePreview,
  type OpenFilePreview,
} from '@/features/chat/file-preview-host';
import { useFileLink } from '@/hooks/useFileLink';

import type { ReactNode } from 'react';

interface InlineFileLinkProps {
  token: string;
  href: string;
  children: ReactNode;
}

export const InlineFileLink = ({
  token,
  href,
  children,
}: InlineFileLinkProps) => {
  const openFilePreview = useOpenFilePreview();

  if (!openFilePreview)
    return <PermanentLink href={href}>{children}</PermanentLink>;

  return (
    <PreviewableFileLink token={token} href={href} onOpen={openFilePreview}>
      {children}
    </PreviewableFileLink>
  );
};

const PreviewableFileLink = observer(
  ({
    token,
    href,
    onOpen,
    children,
  }: InlineFileLinkProps & { onOpen: OpenFilePreview }) => {
    const state = useFileLink(token);

    if (state.status === 'unavailable')
      return <PermanentLink href={href}>{children}</PermanentLink>;
    if (state.status === 'loading') return <ArtifactCardSkeleton />;

    return (
      <div
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <ArtifactCard file={state.file} onOpen={onOpen} />
      </div>
    );
  }
);

const PermanentLink = ({
  href,
  children,
}: Pick<InlineFileLinkProps, 'href' | 'children'>) => (
  <a href={href} rel="noopener noreferrer">
    {children}
  </a>
);
